// Core
import { useState } from "react";
import { AxiosError } from "axios";
import { useTranslations } from "next-intl";

// Definitions
import { ErrorResponse, IRegisterFieldInput, UseAuthType } from "../types";

// Utils
import { authApi } from "@/domains/auth/service";

interface UseAuthRegisterType extends UseAuthType {
  onMutateAuth: (mutatePayload: IRegisterFieldInput) => Promise<{
    code?: number;
    success?: boolean;
  }>;
}

export const useAuthRegister = (): UseAuthRegisterType => {
  const [error, setError] = useState<ErrorResponse["violations"]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const tValidation = useTranslations("Validation");

  const onMutateAuth = async (mutatePayload: IRegisterFieldInput): Promise<{ code?: number }> => {
    setLoading(true);
    setError([]);

    try {
      const res = await authApi.fetchRegister({
        data: {
          country: mutatePayload.country,
          password: mutatePayload.password,
          phone: mutatePayload.phoneInput,
          isConfirmConditions: true,
          profile: {
            country: mutatePayload.country,
            currency: mutatePayload.currency,
            utmParams: mutatePayload.utmParams,
            promoId: mutatePayload.promoId ? mutatePayload.promoId : null,
          },
        },
      });
      return res.data as { code: number };
    } catch (e: unknown) {
      const _error = e as AxiosError<ErrorResponse>;
      if (_error.response?.data) {
        const errorData = _error.response.data;
        if (errorData.violations) {
          setError(errorData.violations);
        }
      }
      return {};
    } finally {
      setLoading(false);
    }
  };
  const renderErrorMessage = (
    propertyName: string,
    authError: ErrorResponse["violations"],
  ): string | null => {
    const foundError = authError?.find((_error) => _error.property === propertyName);
    return foundError ? tValidation(foundError.error) : null;
  };

  return {
    error,
    onMutateAuth,
    loading,
    renderErrorMessage,
  };
};
